import {action, makeAutoObservable, reaction, runInAction} from "mobx";
import {ApiClient} from "../api/api-client";
import {
  CarService,
  CarWidgetInfo,
  DeliveryOptions,
  FormContactCar,
} from "../types/widget-types";
import {extractUrlOfCustomer} from "../utils/parser-url";
import {FORM_ERROR} from "final-form";
import {
  validateGoogleAddress
} from "../utils/validate-google-address";
import {WidgetConfig} from "../config/widget-config";

export type FormCarType = {
  plateNumber: string;
  service: string;
  description: string;
};

export type ConcreteDetails = {
  type: string;
  quantity: string;
  details?: string;
};

export enum CarStep {
  FormVehicle,
  FormDetails,
  FormThanks,
}

const transferEventMap = {
  [CarStep[CarStep.FormVehicle]]: "searchButton",
  [CarStep[CarStep.FormDetails]]: "confirmButton",
} as const;

export class CarStore {
  _leadId = "";
  formInfo: Partial<FormContactCar> = {};
  carInfo?: CarWidgetInfo;
  carServices: CarService[] = [];
  carDeliveries: DeliveryOptions[] = [];
  formAggregate: Partial<FormCarType> = {};
  step: CarStep = CarStep.FormVehicle;
  directionSlide: "left" | "right" = "left";
  plateNumber?: string;
  isSearchingVehicle = false;
  quoteValues: Partial<ConcreteDetails> = {}

  constructor(public readonly config:WidgetConfig = {}) {
    makeAutoObservable(
      this,
      {
        config: false,
        validateFieldsContact: false,
      },
      {
        autoBind: true,
      }
    );
  }

  private clearPrevFields() {
    this.plateNumber = undefined;
    this.carInfo = undefined;
    this.formAggregate = {};
    this.formInfo = {};
    this.carServices = [];
    this.carDeliveries = [];
  }

  nextStep() {
    this.directionSlide = "left";
    this.trackButtonClick();
    this.step++;
  };

  backStep() {
    if (this.step === 0) {
      return;
    }
    if (!this.isDefineService) {
      this.directionSlide = "right";
      this.step = CarStep.FormVehicle;
      return;
    }
    this.directionSlide = "right";
    this.step--;
  };

  private isCachedPlateNumber(number: string) {
    if (this.plateNumber?.includes(number) && number.length <= 7) {
      return true;
    }
    return number === this.plateNumber;
  }

  async findVehicle(plate: string) {
    const plateSplit = plate.replace(/\s+/g, "");
    if (this.isCachedPlateNumber(plateSplit)) {
      return;
    }
    this.plateNumber = plateSplit;
    this.isSearchingVehicle = true;
    try {
      const carInfo = await ApiClient.shared.searchRegisterNumber(plateSplit);
      runInAction(() => {
        this.carInfo = carInfo;
      });
    } catch {
      this.carInfo = undefined;
    } finally {
      runInAction(() => (this.isSearchingVehicle = false));
    }
  }

  get vehicleFullName() {
    return this.carInfo
      ? Object.values(this.carInfo).join(" ")
      : "Vehicle undefined";
  }

  get deliveryOptions() {
    return this.carDeliveries.map((delivery) => {
      return {
        value: delivery.name,
        label:
          delivery.type !== "delivery"
            ? `${delivery.name}, ${delivery.address}`
            : "My location",
      };
    });
  }

  get sites() {
    return this.carDeliveries.map((delivery) => {
      return {
        value: delivery.name,
        label:
          delivery.type !== "delivery"
            ? `${delivery.name}, ${delivery.address}`
            : "My location",
      };
    });
  }

  get isDefineService() {
    return this.carServices.length > 0;
  }

  public loadServices() {
    ApiClient.shared
      .getServices()
      .then(action((listServices) => (this.carServices = listServices)));
  }

  private async loadDeliveries() {
    ApiClient.shared.getDeliveryOptions().then(
      action((res) => {
        this.carDeliveries = res;
      })
    );
  }


  updateFormQuotes(values: ConcreteDetails) {
    this.quoteValues = values;
    /**
     * categories  = type
     * description = quantity + details
     * @see arrangeLead
     */
    //
    this.formAggregate = {
      description: values.quantity + (values.details ? ` details: ${values.details}` : ''),
      service: values.type
    }
    this.nextStep();
  }


  updateFormVehicle(values: FormCarType) {
    this.formAggregate = values;
  }

  public arrangeLead = async () => {
    if (!this.formAggregate) {
      return
    }
    const order = {
      category: this.formAggregate.service,
      ...(this.plateNumber && {plateNumber: this.plateNumber}),
      description: this.formAggregate.description,
      addressType: 1,
      ...this.formInfo,
      ...this.carInfo,
      ...extractUrlOfCustomer(),
    };
    const {leadId} = await ApiClient.shared.makeOrder(order);
    runInAction(() => {
      this._leadId = leadId;
    });
  };

  private notifyManager = async (values: FormContactCar) => {
    if (!this.formAggregate) {
      return
    }
    await ApiClient.shared.contactWithManager({
      ...values,
      addressType: 1,
      plateNumber: this.formAggregate.plateNumber?.replace(/\s+/g, ""),
    });
    runInAction(() => {
      this.step = CarStep.FormThanks;
    });
  };


  validateFieldsContact = (values: FormContactCar) => {
    const errors: Partial<FormContactCar> = {};
    if (!values.name) {
      errors.name = "Required field";
    }
    if (!values.location) {
      errors.location = "Required field";
    }
    if (!values.phone) {
      errors.phone = "Required field";
    }
    return errors;
  };

  async handleFormDetails(values: FormContactCar, shortError = false, withAddressValidation = true) {
    const delivery = this.carDeliveries.find(
      (item) => item.name === values.location
    );

    try {
      const errors =
        await ApiClient.shared.validateContactForm<FormContactCar>({
          addressType: 1,
          ...values,
        });
      if (typeof errors === "string") {
        runInAction(() => this.formInfo = values);
        return;
      }
      if (errors.hasOwnProperty("phone")) {
        if (shortError) {
          errors.phone = 'Invalid phone number'
        } else {
          errors.phone += " (Ex. +44 0203 1234567)";
        }
      }
      if (!delivery && withAddressValidation) {
        const validateAddress = await validateGoogleAddress(values.location);
        if (validateAddress) {
          errors['location'] = validateAddress.location;
        }
      }
      return errors;
    } catch {
      return {[FORM_ERROR]: "Server Error"};
    }
  }

  async submitOrder() {
    try {
      await this.arrangeLead();
      this.nextStep();
    } catch (e) {
      return {
        [FORM_ERROR]: e.message ?? 'Server Error'
      }
    }

  }

  completeCarWidget = () => {
    this.clearPrevFields();
    this.step = CarStep.FormVehicle;
  };

  public trackButtonClick(step = this.step) {
    if (!transferEventMap[CarStep[step]]) {
      return;
    }
    const eventData = JSON.stringify({
      event: transferEventMap[CarStep[step]],
      form: "Booking",
    });
    try {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
        window.console && window.console.log("trackedEventData:", eventData);
      } else {
        window.parent.postMessage(eventData, "*");
      }
    } catch (e) {
      window.console && window.console.log(e);
    }
  }

  public trackSearch(){
    this.trackButtonClick(0);
    this.step = CarStep.FormDetails;
  }
}


